import React from 'react';
import {
  Box,
  Flex,
  Text,
  Badge,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  DrawerFooter,
  FormControl,
  FormLabel,
  Icon,
} from '@chakra-ui/react';
import { Select } from 'chakra-react-select';
import { Funnel } from 'phosphor-react';

interface SelectOption {
  value: string;
  label: string;
}

interface FiltersDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  estadoOptions: SelectOption[];
  municipioOptions: SelectOption[];
  grupoOptions: SelectOption[];
  regimeOptions: SelectOption[];
  tempEstado: SelectOption | null;
  tempMunicipio: SelectOption | null;
  tempGrupo: SelectOption | null;
  tempRegime: SelectOption | null;
  setTempEstado: React.Dispatch<React.SetStateAction<SelectOption | null>>;
  setTempMunicipio: React.Dispatch<React.SetStateAction<SelectOption | null>>;
  setTempGrupo: React.Dispatch<React.SetStateAction<SelectOption | null>>;
  setTempRegime: React.Dispatch<React.SetStateAction<SelectOption | null>>;
  selectedEstado: string | null;
  selectedMunicipio: string | null;
  selectedGrupo: string | null;
  selectedRegime: string | null;
  onApplyFilters: () => void;
  onClearFilters: () => void;
}

export const FiltersDrawer: React.FC<FiltersDrawerProps> = ({
  isOpen,
  onClose,
  estadoOptions,
  municipioOptions,
  grupoOptions,
  regimeOptions,
  tempEstado,
  tempMunicipio,
  tempGrupo,
  tempRegime,
  setTempEstado,
  setTempMunicipio,
  setTempGrupo,
  setTempRegime,
  selectedEstado,
  selectedMunicipio,
  selectedGrupo,
  selectedRegime,
  onApplyFilters,
  onClearFilters,
}) => {
  const hasActiveFilters = selectedEstado || selectedMunicipio || selectedGrupo || selectedRegime;

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px">
          <Flex align="center" gap={2}>
            <Icon as={Funnel} />
            Filtros do Dashboard
          </Flex>
        </DrawerHeader>

        <DrawerBody pt={6}>
          <FormControl mb={6}>
            <FormLabel fontWeight="semibold">Estado (UF)</FormLabel>
            <Select
              placeholder="Selecione um estado..."
              options={estadoOptions}
              value={tempEstado}
              onChange={option => {
                setTempEstado(option);
                setTempMunicipio(null);
              }}
              isClearable
              chakraStyles={{
                control: provided => ({
                  ...provided,
                  borderRadius: 'md',
                }),
              }}
            />
          </FormControl>

          <FormControl mb={6}>
            <FormLabel fontWeight="semibold">Município</FormLabel>
            <Select
              placeholder="Selecione um município..."
              options={municipioOptions}
              value={tempMunicipio}
              onChange={option => setTempMunicipio(option)}
              isClearable
              isDisabled={municipioOptions.length === 0}
              chakraStyles={{
                control: provided => ({
                  ...provided,
                  borderRadius: 'md',
                }),
              }}
            />
            {tempEstado && municipioOptions.length === 0 && (
              <Text fontSize="sm" color="gray.500" mt={1}>
                Nenhum município encontrado para este estado
              </Text>
            )}
          </FormControl>

          <FormControl mb={6}>
            <FormLabel fontWeight="semibold">Unidade (Grupo)</FormLabel>
            <Select
              placeholder="Selecione uma unidade..."
              options={grupoOptions}
              value={tempGrupo}
              onChange={option => setTempGrupo(option)}
              isClearable
              chakraStyles={{
                control: provided => ({
                  ...provided,
                  borderRadius: 'md',
                }),
              }}
            />
          </FormControl>

          <FormControl mb={6}>
            <FormLabel fontWeight="semibold">Regime Tributário</FormLabel>
            <Select
              placeholder="Selecione o regime..."
              options={regimeOptions}
              value={tempRegime}
              onChange={option => setTempRegime(option)}
              isClearable
              chakraStyles={{
                control: provided => ({
                  ...provided,
                  borderRadius: 'md',
                }),
              }}
            />
          </FormControl>

          {hasActiveFilters && (
            <Box p={4} bg="blue.50" borderRadius="md" border="1px solid" borderColor="blue.200">
              <Text fontSize="sm" fontWeight="medium" color="blue.700" mb={2}>
                Filtros ativos:
              </Text>
              <Flex gap={2} flexWrap="wrap">
                {selectedEstado && (
                  <Badge colorScheme="blue" fontSize="sm">
                    Estado: {selectedEstado}
                  </Badge>
                )}
                {selectedMunicipio && (
                  <Badge colorScheme="green" fontSize="sm">
                    Município: {selectedMunicipio}
                  </Badge>
                )}
                {selectedGrupo && (
                  <Badge colorScheme="purple" fontSize="sm">
                    Unidade: {selectedGrupo}
                  </Badge>
                )}
                {selectedRegime && (
                  <Badge colorScheme="orange" fontSize="sm">
                    Regime:{' '}
                    {regimeOptions.find(r => r.value === selectedRegime)?.label || selectedRegime}
                  </Badge>
                )}
              </Flex>
            </Box>
          )}
        </DrawerBody>

        <DrawerFooter borderTopWidth="1px">
          <Button variant="outline" mr={3} onClick={onClearFilters}>
            Limpar Filtros
          </Button>
          <Button colorScheme="blue" onClick={onApplyFilters}>
            Aplicar Filtros
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
